import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaEuroSign,
  FaClock,
  FaReceipt,
} from 'react-icons/fa';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import { CheckOnboardingDto, PayoutDetails, PayoutsMetadata } from '../../../models/marketplace.model';
import {
  checkOnboarding,
  getAccountBalance,
  getLoginToDashBoardLoginLink,
} from '../../../services/community/merchant.marketplace.service';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
} from '../../components/tables/BaseTable';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
import { getPayoutTransactionsForCommunity } from '../../../services/community/orders.marketplace.service';

export default function MerchantAccountPage(): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.merchantAccount' });
  const navigate = useNavigate();
  const [onboardStatus, setOnboardStatus] = useState<CheckOnboardingDto | null>(null);
  const [payoutHistory, setPayoutHistory] = useState<PayoutDetails[]>([]);
  const [payoutsMetadata, setPayoutsMetadata] = useState<PayoutsMetadata>({
    totalPayouts: 0,
    totalAmount: 0,
    totalFees: 0,
    totalIncomingTransactions: 0,
  });


  useEffect(() => {
    loadOnboardingStatus().then(() => {
      loadPayoutTransactions();
      loadIncomingPayouts();
    });
  }, []);

  const loadIncomingPayouts = async () => {
    try {
      const balance = await getAccountBalance();
      setPayoutsMetadata((prev) => ({ ...prev, totalIncomingTransactions: balance }));
    } catch (error) {
      console.error('Error loading incoming payouts:', error);
    }
  };
  
  const loadPayoutTransactions = async () => {
    try {
      const transactions = await getPayoutTransactionsForCommunity();
      const payouts = transactions.reduce((acc: Record<string, PayoutDetails>, tx) => {
        if (!acc[tx.payoutId]) {
          acc[tx.payoutId] = {
            id: tx.payoutId,
            date: tx.payoutDate.toString(),
            totalAmount: 0,
            totalIncomingTransactions: 0,
            totalFees: 0,
            numberOfTransactions: 0,
          };
        }
        acc[tx.payoutId].totalAmount += tx.netAmount;
        acc[tx.payoutId].totalIncomingTransactions += tx.faceValueAmount;
        acc[tx.payoutId].totalFees += (tx.stripeFeeAmount ?? 0) + (tx.veraFeeAmount ?? 0);
        acc[tx.payoutId].numberOfTransactions += 1;
        return acc;
      }, {});

      setPayoutHistory(Object.values(payouts).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      const totalAmount = Object.values(payouts).reduce((sum, p) => sum + p.totalAmount, 0);
      const totalFees = Object.values(payouts).reduce((sum, p) => sum + p.totalFees, 0);

      setPayoutsMetadata((prev) => ({
        ...prev,
        totalAmount: totalAmount - totalFees,
        totalFees,
        totalPayouts: totalAmount,
      }));
    } catch (error) {
      console.error('Error loading payout transactions:', error);
    }
  };

  const loadOnboardingStatus = async () => {
    try {
      const status = await checkOnboarding();
      setOnboardStatus(status);
    } catch (error) {
      console.error('Error loading onboarding status:', error);
      setOnboardStatus(null);
    }
  };

  const handleLoginToDashBoard = async () => {
    try {
      const response = await getLoginToDashBoardLoginLink();
      window.open(response.url, '_blank');
    } catch (error) {
      console.error('Error getting dashboard login link:', error);
      toast.error(t('toast.failedToAccessDashboard'));
    }
  };

  const handlePayoutRowClick = (payoutId: string) => {
    navigate(`/financial/payout/${payoutId}`);
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Status Overview */}
      <div>
        <h2 className="mb-2 text-lg font-semibold text-primary-900">{t('stripeAccount')}</h2>
        <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {onboardStatus?.onboarded ? (
                <FaCheckCircle className="h-6 w-6 text-success" />
              ) : (
                <FaExclamationTriangle className="h-6 w-6 text-yellow-600" />
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-primary-900">{t('stripeAccount')}</p>
              <p className="text-sm text-primary-700">
                {onboardStatus?.onboarded ? t('connected') : t('notConnected')}
              </p>
            </div>
          </div>

          <div>
            {onboardStatus?.onboarded ? (
              <PrimaryButton
                text={t('goToStripeDashboard')}
                icon={FaExternalLinkAlt}
                onClick={handleLoginToDashBoard}
              />
            ) : onboardStatus?.onboardingLink ? (
              <PrimaryButton
                text={t('continueOnboarding')}
                icon={FaExternalLinkAlt}
                onClick={() => window.open(onboardStatus.onboardingLink, '_blank', 'noopener,noreferrer')}
              />
            ) : (
              <PrimaryButton
                text={t('createAccount')}
                onClick={() => navigate('/financial/sales-collect', { state: { activeTab: 'overview' } })}
              />
            )}
          </div>
        </div>
      </div>

      {onboardStatus?.onboarded && (
        <>
          {/* Payout Overview Cards */}
          <div>
            <h2 className="mb-2 text-lg font-semibold text-primary-900">{t('payoutOverview.title')}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {/* Total Payout */}
              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FaEuroSign className="h-6 w-6 text-primary-300" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">{t('payoutOverview.cards.totalPayout')}</p>
                    <p className="text-xl font-bold text-primary-900">
                      {formatEuroCentsWithSign(payoutsMetadata.totalPayouts - payoutsMetadata.totalFees, i18n.language)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pending Payout */}
              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FaClock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">{t('payoutOverview.cards.pendingPayout')}</p>
                    <p className="text-xl font-bold text-primary-900">
                      {formatEuroCentsWithSign(payoutsMetadata.totalIncomingTransactions, i18n.language)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Fees Paid */}
              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FaReceipt className="h-6 w-6 text-danger" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-primary-900">{t('payoutOverview.cards.feesPaid')}</p>
                    <p className="text-xl font-bold text-danger">
                      {formatEuroCentsWithSign(payoutsMetadata.totalFees, i18n.language)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payout History Table */}
          <div>
            <h2 className="mb-2 text-lg font-semibold text-primary-900">{t('payoutHistory.title')}</h2>
            <BaseTable>
              <BaseTableHeader>
                <BaseTableHeaderRow>
                  <th className="text-left">{t('payoutHistory.table.date')}</th>
                  <th className="text-right">{t('payoutHistory.table.totalAmount')}</th>
                  <th className="text-right">{t('payoutHistory.table.totalIncomingTransactions')}</th>
                  <th className="text-right">{t('payoutHistory.table.totalFees')}</th>
                  <th className="text-center">{t('payoutHistory.table.numberOfIncomingTransactions')}</th>
                </BaseTableHeaderRow>
              </BaseTableHeader>
              <BaseTableBody>
                {payoutHistory.map((payout, index) => (
                  <BaseTableDataRow
                    key={index}
                    className="group cursor-pointer transition-all hover:bg-secondary-200"
                    onClick={() => handlePayoutRowClick(payout.id)}>
                    <td className="py-3 text-left">
                      {new Date(payout.date).toLocaleDateString(i18n.language)}
                    </td>
                    <td className="py-3 text-right">
                      <span className="font-medium text-primary-900">
                        {formatEuroCentsWithSign(payout.totalAmount, i18n.language)}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <span className="font-medium text-success">
                        {formatEuroCentsWithSign(payout.totalIncomingTransactions, i18n.language)}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <span className="font-medium text-danger">
                        {formatEuroCentsWithSign(payout.totalFees, i18n.language)}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      <span className="font-medium text-primary-900">{payout.numberOfTransactions}</span>
                    </td>
                  </BaseTableDataRow>
                ))}
              </BaseTableBody>
            </BaseTable>
          </div>
        </>
      )}
    </div>
  );
}
